import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useLinkedChildren } from '../../lib/queries/parent/useLinkedChildren';
import { ChildAvatarHero } from './ChildAvatarHero';
import { ChildChooserStrip } from './ChildChooserStrip';
import { WelfareCard } from './WelfareCard';
import './ParentShell.css';

/**
 * Parent Portal chrome — the equivalent of <AppShell> for the coach
 * surface. Three regions:
 *
 *   1. Sidebar — child avatar hero (single) or chooser strip (multi)
 *      + nav (Home / Settings / Resources). Glass-treated, mirrors
 *      AppShell's sidebar idiom.
 *   2. Main — full-bleed `<Outlet>` for the parent route children.
 *      `data-density="rest"` loosens spacing vs the coach app.
 *   3. WelfareCard footer — always-present safeguarding callout
 *      (regulatory mandate per §22.13).
 *
 * The active child id is held here in state — the chooser updates it
 * and child views read it via the Outlet context (or a future
 * useActiveChild hook).
 */

export function ParentShell() {
  const { data: children, loading } = useLinkedChildren();
  const [activeChildId, setActiveChildId] = useState<string | null>(null);

  // Default to the first linked child once the list lands.
  useEffect(() => {
    if (!activeChildId && children.length > 0) setActiveChildId(children[0].id);
  }, [children, activeChildId]);

  const activeChild = children.find((c) => c.id === activeChildId) ?? null;

  return (
    <div className="parent-shell" data-density="rest">
      <aside className="parent-shell__sidebar" aria-label="Parent navigation">
        {loading ? (
          <div className="parent-shell__sidebar-loading">Loading…</div>
        ) : children.length === 0 ? (
          <div className="parent-shell__no-children">
            <div className="parent-shell__eyebrow">No linked children yet</div>
            <p>
              Set up your child's account so you can see their progress here.
            </p>
            <Link to="/parent/child/setup" className="parent-shell__cta">
              Set up child's account
            </Link>
          </div>
        ) : children.length === 1 && activeChild ? (
          <ChildAvatarHero child={activeChild} size="lg" active />
        ) : (
          <>
            {activeChild && <ChildAvatarHero child={activeChild} size="lg" active />}
            <ChildChooserStrip
              children={children}
              activeId={activeChildId}
              onSelect={setActiveChildId}
            />
          </>
        )}

        <nav className="parent-shell__nav" aria-label="Parent menu">
          <div className="parent-shell__eyebrow">Menu</div>
          <NavLink to="/parent" end className={({ isActive }) => `parent-shell__nav-item${isActive ? ' parent-shell__nav-item--active' : ''}`}>
            Home
          </NavLink>
          <NavLink to="/parent/resources" className={({ isActive }) => `parent-shell__nav-item${isActive ? ' parent-shell__nav-item--active' : ''}`}>
            Safeguarding resources
          </NavLink>
          <NavLink to="/parent/settings" className={({ isActive }) => `parent-shell__nav-item${isActive ? ' parent-shell__nav-item--active' : ''}`}>
            Privacy &amp; consent
          </NavLink>
        </nav>

        <div className="parent-shell__footer">
          <Link to="/legal/privacy" className="parent-shell__legal-link">
            Privacy notice
          </Link>
          <Link to="/legal/privacy-for-young-players" className="parent-shell__legal-link">
            For young players
          </Link>
        </div>
      </aside>

      <main className="parent-shell__main">
        <Outlet context={{ activeChildId, children }} />
      </main>

      <aside className="parent-shell__rail" aria-label="Safeguarding">
        <WelfareCard
          welfareOfficer={null /* Wave 3.4 wires the active child's WO */}
        />
      </aside>
    </div>
  );
}
